import type { JSX, MutableRefObject } from 'react';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SessionCanvas from '../SessionCanvas';
import FloatingToolbar from '../FloatingToolbar';
import PageManager from '../domain/PageManager';
import { useSessionCanvasController } from '../../hooks/useSessionCanvasController';
import { useStudioMapPaneContent, useStudioMapShortcuts } from './useStudioMapPane';
import { useStudioMapPanes } from './StudioMapPanes';
import type { StudioMapControl } from './StudioMapMenu';
import { PageViewScope, useSessionContext } from '../../contexts/SessionContext';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { useTokenContext } from '../../contexts/TokenContext';
import { useStudioMapLink } from '../../contexts/StudioMapLinkContext';
import { worldToScreen } from '../../utils';
import type { MapPin } from '@roll1/shared';
import { useI18n } from '../../i18n';
import TokenPanel from '../TokenPanel';
import PinsPanel from '../PinsPanel';
import MapObjectPanel from '../MapObjectPanel';
import { UndoProvider } from '../../contexts/UndoContext';
import { ToolProvider } from '../../contexts/ToolContext';
import { CanvasProvider } from '../../contexts/CanvasContext';
import { CanvasInteractionProvider } from '../../contexts/CanvasInteractionContext';
import { SelectionProvider } from '../../contexts/SelectionContext';
import { TokenProvider } from '../../contexts/TokenContext';
import { ControlledTokensSync } from '../../hooks/useControlledTokensSync';
import { MapPaneContext, type MapPaneValue } from '../../contexts/MapPaneContext';
import { ActorProvider } from '../../contexts/ActorContext';
import { DrawingSelectionProvider } from '../../contexts/DrawingSelectionContext';
import { EditPageProvider } from '../../contexts/EditPageContext';
import type { PageOpsContextValue } from '../../contexts/PageOpsContext';

const AiMapDialog = lazy(() => import('../AiMapDialog'));

/**
 * A live, interactive working map of ONE page inside GM Studio — the full
 * table tool set (tokens, drawing, fog, walls, notes, pins, text, map
 * objects) through the shared `<SessionCanvas>` cluster.
 *
 * Studio shows up to two of them side by side (focus + pinned pane), so each
 * is an isolated instance (GM Studio package 5.3) — mount it keyed by
 * `pageId`: one instance holds one page for its whole life.
 *
 * Owned by the pane (its own providers below): the canvas engine and camera,
 * the content hooks (tokens / notes / pins / drawing / fog / walls / links /
 * map objects / text), the selection, the canvas interaction registry, the
 * undo stack and the active tool + its options. `PageViewScope` makes the
 * pane's page THE page of everything inside — the viewed page, the edit
 * target, the page commands carry, also when sent later (undo, batched
 * erase). The pane never touches the table's view (`viewingPageId`).
 *
 * Shared with the campaign: WS, the page store and the players' map, sheets,
 * journal, chat, settings, the shortcut registry. Shared with the other pane
 * (StudioMapPanes, once per Studio): the echo router — page-content echoes
 * reach the pane holding the page exactly once — the working map (the last
 * one clicked/focused; canvas hotkeys act there only, outlined when two maps
 * are open) and pan/zoom remembered per page.
 *
 * Edits persist through `mp.send` on the pane's page by the existing rules —
 * an own view is not a secret draft: another GM sees them at once; players
 * see them when the page is shown to them (a separate, confirmed action), or
 * at once if this page IS the players' map. A location link here opens its
 * target in the workspace (`onOpenPage`), it never moves the players.
 */
export interface StudioMapCanvasProps {
  pageId: string;
  /** Token → its sheet in the Studio workspace (double click / «Open sheet»). */
  onOpenSheet?: (sheetId: string) => void;
  /** Pin → its journal entry in the Studio workspace (pin editor «open entry»). */
  onOpenJournalEntry?: (entryId: string) => void;
  /** Location link → its target page in the Studio workspace (never moves the players). */
  onOpenPage?: (pageId: string) => void;
  /** Toolbar AI toggle drives the Studio AI column. */
  aiOpen?: boolean;
  onToggleAi?: () => void;
  /** The header's «⋯» reaches the pane's own actions (clear drawings, AI map, drag the image) through this. */
  controlRef?: MutableRefObject<StudioMapControl | null>;
  /** «Содержимое карты» (F-5): this map's tokens, pins and objects in a drawer at the right of the pane. */
  drawerOpen?: boolean;
}

/** The smallest height a Studio map is squeezed to (px). */
const STUDIO_MAP_MIN_HEIGHT = 240;

export function StudioMapCanvas(props: StudioMapCanvasProps): JSX.Element {
  return (
    <PageViewScope pageId={props.pageId} onOpenPage={props.onOpenPage}>
      <UndoProvider>
        <ToolProvider>
          <CanvasProvider>
            <CanvasInteractionProvider>
              <SelectionProvider outgoingPageId={props.pageId}>
                <TokenProvider>
                  <ControlledTokensSync />
                  <StudioMapPane {...props} />
                </TokenProvider>
              </SelectionProvider>
            </CanvasInteractionProvider>
          </CanvasProvider>
        </ToolProvider>
      </UndoProvider>
    </PageViewScope>
  );
}

/**
 * P09b (Q-7): the ring on a pin that is lit from its note (hover / the badge). Drawn over the canvas and followed with
 * the camera while it is lit — a DOM mark, so the map's own drawing is untouched.
 */
function PinHighlight({ pin }: { readonly pin: MapPin }): JSX.Element | null {
  const { engine } = useCanvasContext();
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    let raf = 0;
    const tick = (): void => {
      const p = worldToScreen(pin.x, pin.y, engine.cameraRef.current);
      setPos((prev) => (prev !== null && prev.x === p.x && prev.y === p.y ? prev : { x: p.x, y: p.y }));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return (): void => cancelAnimationFrame(raf);
  }, [engine, pin.x, pin.y]);
  if (pos === null) return null;
  return (
    <span
      aria-hidden="true"
      data-testid="studio-pin-highlight"
      className="pointer-events-none absolute z-20 h-10 w-10 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full ring-2 ring-hl"
      style={{ left: pos.x, top: pos.y }}
    />
  );
}

function StudioMapPane({ pageId, onOpenSheet, onOpenJournalEntry, onOpenPage, aiOpen, onToggleAi, controlRef, drawerOpen }: StudioMapCanvasProps): JSX.Element {
  const panes = useStudioMapPanes();
  const { t } = useI18n();
  const { mp } = useSessionContext();
  const { engine, drawing } = useCanvasContext();
  const [aiMapOpen, setAiMapOpen] = useState<boolean>(false);
  const [movingImage, setMovingImage] = useState<boolean>(false);
  const { saveCurrentPageToStore, loadPageToHooks } = useStudioMapPaneContent(pageId);
  const controller = useSessionCanvasController();
  const pageOpsRef = useRef<PageOpsContextValue | null>(null);
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  // The pin lit from its note: only if it is on THIS map.
  const link = useStudioMapLink();
  const { pins } = useTokenContext();
  const litPin = link?.highlightPinId !== undefined ? pins.pins.find((p) => p.id === link.highlightPinId) : undefined;

  const isWorking = panes.isWorking;
  const ownsKeyboard = useCallback((): boolean => isWorking(pageId), [isWorking, pageId]);
  const paneValue = useMemo<MapPaneValue>(() => ({ pageId, ownsKeyboard, root }), [pageId, ownsKeyboard, root]);
  useStudioMapShortcuts(pageId, ownsKeyboard);

  // What the header's «⋯» can ask of this pane (it sits outside the pane's providers).
  useEffect(() => {
    if (controlRef === undefined) return;
    const control: StudioMapControl = {
      clearDrawings: (): void => controller.clearAllDrawingsRef.current(),
      openAiMap: (): void => setAiMapOpen(true),
      startMoveImage: (): void => setMovingImage(true),
    };
    controlRef.current = control;
    return (): void => {
      if (controlRef.current === control) controlRef.current = null;
    };
  }, [controlRef, controller]);

  // «Двигать картинку»: dragging on the map moves its image; each release is saved (page:update-settings, the
  // pane's page). Esc leaves. The offset is in image pixels: on screen it is multiplied by the map scale and the zoom.
  useEffect(() => {
    if (!movingImage) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMovingImage(false);
    };
    window.addEventListener('keydown', onKey);
    return (): void => window.removeEventListener('keydown', onKey);
  }, [movingImage]);
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const moveImage = {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>): void => {
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = { x: e.clientX, y: e.clientY, ox: engine.mapOffsetRef.current.x, oy: engine.mapOffsetRef.current.y };
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>): void => {
      const start = dragRef.current;
      if (start === null) return;
      const k = engine.cameraRef.current.zoom * engine.mapScaleRef.current;
      engine.mapOffsetRef.current = {
        x: Math.round(start.ox + (e.clientX - start.x) / k),
        y: Math.round(start.oy + (e.clientY - start.y) / k),
      };
      engine.markAllDirty();
    },
    onPointerUp: (): void => {
      if (dragRef.current === null) return;
      dragRef.current = null;
      const { x, y } = engine.mapOffsetRef.current;
      if (mp.session) mp.send({ type: 'page:update-settings', pageId, changes: { mapOffsetX: x, mapOffsetY: y } } as never);
    },
  };

  // The first ordinary press or focus anywhere in the pane (canvas, toolbar,
  // zoom buttons) makes it the working map — and the same press acts here.
  const setWorking = panes.setWorking;
  const work = useCallback((): void => setWorking(pageId), [setWorking, pageId]);
  const outlined = panes.paneCount > 1 && panes.workingPageId === pageId;

  return (
    <MapPaneContext.Provider value={paneValue}>
      <div
        ref={setRoot}
        data-testid="studio-map-canvas"
        data-page-id={pageId}
        data-working-map={panes.workingPageId === pageId ? 'true' : 'false'}
        data-highlight-pin={litPin?.id}
        onPointerDownCapture={work}
        onFocusCapture={work}
        className={outlined ? 'rounded-md outline outline-2 outline-offset-1 outline-hl/70' : undefined}
        // The map fills its pane under the pane's header (package 5.4; was a
        // fixed 70vh): flex 1 in the PageEntityView body. Below the floor a
        // very short window scrolls the pane body instead of squeezing the
        // canvas; the engine follows the container's size (ResizeObserver).
        style={{ position: 'relative', display: 'flex', alignSelf: 'stretch', width: '100%', flex: '1 1 0%', minHeight: STUDIO_MAP_MIN_HEIGHT }}
      >
        {/* Tool palette of THIS map: the tool and its options are the pane's
            (ToolProvider above). Contained in the pane (never over the rail /
            tabs / other pane). Initiative and dice are table panels Studio
            does not have → not shown; AI opens the Studio AI column. */}
        <FloatingToolbar
          placement="contained"
          {...(onToggleAi !== undefined ? { onAiToggle: onToggleAi, aiOpen: aiOpen === true } : {})}
          toolOptionsProps={{
            onAoeStartPlacement: controller.aoePlacement.startPlacement,
            onAoeClearAll: controller.aoeTemplates.removeAllTemplates,
          }}
        />
        {/* EditPageProvider: the edit target + «open a page in the workspace».
            ActorProvider is AppShell-local (not above the fork), so the pane
            provides its own for CanvasArea's portraits. DrawingSelectionProvider
            + PageManager complete the provider trio the cluster needs. */}
        <EditPageProvider pageId={pageId} onOpenPage={onOpenPage}>
          <ActorProvider>
            <DrawingSelectionProvider
              drawingSelection={controller.drawingSelection}
              clearAllDrawingsRef={controller.clearAllDrawingsRef}
            >
              <PageManager
                saveCurrentPageToStore={saveCurrentPageToStore}
                loadPageToHooks={loadPageToHooks}
                handleTokenUpload={controller.handleTokenUpload}
                pageOpsRef={pageOpsRef}
              >
                <SessionCanvas
                  controller={controller}
                  pageId={pageId}
                  showCharacterHud={false}
                  onOpenSheet={onOpenSheet}
                  onOpenJournalEntry={onOpenJournalEntry}
                />
                {drawerOpen === true ? (
                  // F-5: inside the pane's own providers, so it lists THIS map (never the table's).
                  <aside
                    aria-label={t('studio.mapDrawer.open')}
                    className="flex w-72 flex-none flex-col gap-2 overflow-y-auto border-l border-line bg-surface p-2"
                    data-testid="studio-map-drawer"
                  >
                    {/* Sidebar polish: each panel carries its own section header (Токены · Маркеры · Объекты, count, ＋). */}
                    <TokenPanel onAddToken={controller.handleTokenUpload} onOpenSheet={onOpenSheet} />
                    <PinsPanel pageId={pageId} />
                    <MapObjectPanel />
                  </aside>
                ) : null}
              </PageManager>
            </DrawingSelectionProvider>
          </ActorProvider>
        </EditPageProvider>
        {litPin !== undefined ? <PinHighlight pin={litPin} /> : null}
        {movingImage ? (
          <div
            className="absolute inset-0 z-30 cursor-move touch-none"
            data-testid="studio-map-move-image"
            {...moveImage}
          >
            <span className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 rounded-md border border-hl/50 bg-surface/90 px-3 py-1 text-meta text-hl">
              {t('studio.mapDialog.moveImageActive')}
            </span>
          </div>
        ) : null}
        {aiMapOpen && mp.session && mp.participant ? (
          <Suspense fallback={null}>
            <AiMapDialog
              open={aiMapOpen}
              onOpenChange={setAiMapOpen}
              strokesRef={drawing.strokes}
              mapWidth={engine.mapWidth}
              mapHeight={engine.mapHeight}
              sessionId={mp.session.id}
              participantId={mp.participant.id}
              pageId={pageId}
            />
          </Suspense>
        ) : null}
      </div>
    </MapPaneContext.Provider>
  );
}
